import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, StatusBadge, Text,
} from '@domain-group/suit-theme'

import { digObject, formatDate } from '@domain-group/javascript-utils'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import useApiLog, { useApiLogForm } from '@hooks/useApiLog'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const parseJson = (string: string) => {
  try {
    const modifiedString = string.replace(/=>/g, ':').replace(/\\/g, '').replace(/nil/g, 'null')
    const json = JSON.parse(modifiedString)
    return JSON.stringify(json, null, 4)
  } catch {
    return string
  }
}

const MODAL_KEY = 'EditApiLogModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateApiLog: (payload: HandleCallbackActionParams) => void,
}

interface ModalProps {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const EditApiLogModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateApiLog } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { apiLog: initApiLog } = modalPayload

  const {
    apiLog,
    callbacks: { updateApiLog: createFn },
    updating,
    urls: { exportApiLog },
    user,
  } = useApiLog(initApiLog)

  const {
    entityState,
    entityState: {
      notes,
    },
    handlers,
    saveEnabled,
  } = useApiLogForm(apiLog, { validateOn: 'change' })

  const {
    actioned_by_id,
    api_type,
    dates,
    entity_id,
    entity_type,
    event,
    http_method,
    request_body,
    response_body,
    status_code,
    webhook_id,
    url,
  } = apiLog

  const success = digObject(apiLog, 'success', false)

  const updateApiLogPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.apiLog,
    toastText: 'API Log Updated',
  }

  const fullName = digObject(user, 'full_name', '')

  const createdAt = digObject(dates!, 'created.date_time', '')
  const formattedDateformatDate = formatDate(createdAt, 'dd/MM/yyyy HH:mm', "ccc dd LLL y 'at' hh:mma")

  const updatedAt = digObject(dates!, 'updated.date_time', '')
  const formattedUpdateDate = formatDate(updatedAt, 'dd/MM/yyyy HH:mm', "'at' h:mma 'on' dd/MM/yyyy")

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Edit" titleSecondLine="API Log" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Date">
            <Text fontSize="small">{formattedDateformatDate}</Text>
          </Form.Field>

          <Form.Field label="Status" marginTop="large">
            <Text fontSize="small">
              <StatusBadge
                color="faintGrey"
                // @ts-expect-error TS2322
                text={status_code}
              />
            </Text>
          </Form.Field>

          <Form.Field label="Success" marginTop="large">
            <Text fontSize="small">
              <StatusBadge
                color={success ? 'green' : 'red'}
                text={success.toString()}
              />
            </Text>
          </Form.Field>

          <Form.Field label="Entity" marginTop="large">
            <Text fontSize="small">{`${entity_type} #${entity_id}`}</Text>
          </Form.Field>

          <Form.Field label="API Type" marginTop="large">
            <Text fontSize="small">{api_type}</Text>
          </Form.Field>

          <Form.Field label="Event" marginTop="large">
            <Text fontSize="small">{event}</Text>
          </Form.Field>

          <Form.Field label="Method" marginTop="large">
            <Text fontSize="small">{http_method}</Text>
          </Form.Field>

          <Form.Field label="Webhook" marginTop="large">
            <Text fontSize="small">
              {webhook_id ? `Webhook #${webhook_id}` : 'Not Set'}
            </Text>
          </Form.Field>

          <Form.Field label="URL" marginTop="large">
            <Text fontSize="small" style={{ overflowWrap: 'anywhere' }}>
              {url}
            </Text>
          </Form.Field>

          {!!actioned_by_id && (
            <Form.Field label="Last Actioned By" marginTop="large">
              <Text fontSize="small">{fullName} {formattedUpdateDate}</Text>
            </Form.Field>
          )}

          <Form.Field label="Notes" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="notes" style={{ height: 120 }} value={notes} {...handlers} />
          </Form.Field>

          {!!request_body && (
            <Form.Field
              label="Request Body"
              marginTop="large"
            >
              <textarea
                disabled
                style={{ height: 120, resize: 'vertical' }}
                value={parseJson(request_body)}
              />
            </Form.Field>
          )}

          {!!response_body && (
            <Form.Field
              label="Response Body"
              marginTop="large"
            >
              <textarea
                disabled
                style={{ height: 120, resize: 'vertical' }}
                value={parseJson(response_body)}
              />
            </Form.Field>
          )}

          <Form.Field label="More Options" marginTop="large">
            <AuthenticatedDownload>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faDownload} />}
                // @ts-expect-error TS2322
                href={exportApiLog}
                size="medium"
              >
                Download
              </Button>
            </AuthenticatedDownload>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateApiLog(updateApiLogPayload)}
          size="large"
        >
          Update Api Log
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditApiLogModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
